import { cache } from 'react'

import { apiPaths, buildPublicApiUrl, fetchPublicApiJson } from '@/lib/api/public-api'

export const CAREERS_FETCH = {
  revalidateShort: 300,
  defaultPageSize: 25,
} as const


export type CareerApiItem = {
  id: number
  documentId: string
  title: string
  slug: string
  location: string | null
  workMode: string | null
  employmentType: string | null
  level: string | null
  summary: string | null
  content: string | null
  applyEmail: string | null
  applyUrl: string | null
  sortOrder: number
  active: boolean
  status: number | null
  createdAt: string
  updatedAt: string
  publishedAt: string
}

export type CareersApiResponse = {
  data: CareerApiItem[]
  meta?: {
    pagination?: {
      page: number
      pageSize: number
      pageCount: number
      total: number
    }
  }
}

export type CareerDetailApiItem = CareerApiItem

export type CareerDetailApiResponse = {
  data: CareerDetailApiItem
  meta?: unknown
}

export type RoleStatusKey = 'open' | 'urgent-hiring' | 'on-hold' | 'filled' | 'closed'

export const ROLE_STATUS_BY_VALUE: Record<number, RoleStatusKey> = {
  0: 'open',
  1: 'urgent-hiring',
  2: 'on-hold',
  3: 'filled',
  4: 'closed',
}

export const ROLE_STATUS_TONE: Record<RoleStatusKey, string> = {
  open: 'border-emerald-500/30 bg-emerald-50 text-emerald-700',
  'urgent-hiring': 'border-brand-red/30 bg-brand-red/5 text-brand-red',
  'on-hold': 'border-amber-500/30 bg-amber-50 text-amber-700',
  filled: 'border-blue-500/30 bg-blue-50 text-blue-700',
  closed: 'border-charcoal-300 bg-charcoal-50 text-charcoal-500',
}

export const ROLE_STATUS_TEXT_TONE: Record<RoleStatusKey, string> = {
  open: 'text-emerald-700',
  'urgent-hiring': 'text-brand-red',
  'on-hold': 'text-amber-700',
  filled: 'text-blue-700',
  closed: 'text-charcoal-500',
}

export function getRoleStatusKey(role: Pick<CareerApiItem, 'status'>): RoleStatusKey | null {
  if (role.status == null) return null
  return ROLE_STATUS_BY_VALUE[role.status] ?? null
}

export type CareersListOptions = {
  departmentSlug?: string
  pageSize?: number
}

export function buildCareersListParams(options?: CareersListOptions): Record<string, string> {
  const params: Record<string, string> = {
    'sort[0]': 'sortOrder:asc',
    'pagination[pageSize]': String(options?.pageSize ?? CAREERS_FETCH.defaultPageSize),
  }
  if (options?.departmentSlug) params.departmentSlug = options.departmentSlug
  return params
}

export async function fetchCareersFromPublicApi(
  options?: CareersListOptions & { revalidateSeconds?: number },
) {
  const url = buildPublicApiUrl(apiPaths.careers, buildCareersListParams(options))
  const tags = ['api:careers']
  if (options?.departmentSlug) tags.push(`api:careers:dept:${options.departmentSlug}`)

  return fetchPublicApiJson<CareersApiResponse>(url, {
    revalidateSeconds: options?.revalidateSeconds ?? CAREERS_FETCH.revalidateShort,
    tags,
  })
}

export async function fetchCareerBySlugFromPublicApi(
  slug: string,
  options?: { revalidateSeconds?: number },
) {
  const safe = slug.trim()
  if (!safe) throw new Error('Missing career slug')

  const url = buildPublicApiUrl(apiPaths.careerBySlug(safe))
  return fetchPublicApiJson<CareerDetailApiResponse>(url, {
    revalidateSeconds: options?.revalidateSeconds ?? CAREERS_FETCH.revalidateShort,
    tags: ['api:careers', `api:careers:${safe}`],
  })
}

export const getCareerBySlug = cache((slug: string) =>
  fetchCareerBySlugFromPublicApi(slug).catch(() => undefined),
)
